import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import { TableComponent } from '../../TableComponent_v2';
import useWidgetData from '../hooks/useWidgetData';
import dayjs from 'dayjs';
import { DEFAULT_FORECAST_TABLE_MEASURES } from '../../table/measureRegistry';
import WidgetEmptyState from '../WidgetEmptyState';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

// Pre-define icons at module level (babel macro requires static values)
const tableIcon = icon({ name: 'table', family: 'sharp', style: 'light' });

/**
 * ForecastTableWidget - Shows forecast data in a period-based table view
 * Each row represents a forecasted period with cost, change, and trend indicators
 * Uses TableComponent/AnalyzerTable_v2 with selectable measures like cost by resource table
 */
const ForecastTableWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false,
  onFilterAdd
}) => {
  const { data, isLoading, error } = useWidgetData(config, filters);

  // Transform forecast data - aggregate by resource for current month vs previous month
  const transformedData = useMemo(() => {
    if (!data || !data.CloudCost || !data.CloudCost.details) {
      return [];
    }

    // Get current month boundaries
    const now = dayjs();
    const currentMonthStart = now.startOf('month');
    const previousMonthStart = currentMonthStart.subtract(1, 'month').startOf('month');

    // Extract details from API response
    const details = data.CloudCost.details.map((d) => {
      // Handle both API response format and already transformed format
      if (d.groupings && Array.isArray(d.groupings)) {
        return {
          time: d.groupings[0]?.value || d.time,
          grouping: d.groupings[1]?.value || d.grouping || 'Total',
          measure: d.measures?.[0]?.value ?? d.measure ?? 0,
          forecast: d.measures?.[1]?.value ?? d.forecast ?? 0
        };
      }
      // Already transformed format
      return {
        time: d.time,
        grouping: d.grouping || 'Total',
        measure: d.measure ?? 0,
        forecast: d.forecast ?? 0
      };
    });

    // Group by resource and calculate current month and previous month costs
    const resourceMap = new Map();
    
    details.forEach((d) => {
      const resourceName = d.grouping || 'Total';
      const itemDate = dayjs(d.time);
      
      if (!resourceMap.has(resourceName)) {
        resourceMap.set(resourceName, {
          resource: resourceName,
          currentMonthActual: 0,
          currentMonthForecast: 0,
          previousMonthActual: 0
        });
      }
      
      const resource = resourceMap.get(resourceName);
      
      // Check if this data point is in the current month
      const itemMonth = itemDate.format('YYYY-MM');
      const currentMonth = currentMonthStart.format('YYYY-MM');
      const previousMonth = previousMonthStart.format('YYYY-MM');
      
      if (itemMonth === currentMonth) {
        // Current month: sum actual costs (measure) and forecast costs separately
        resource.currentMonthActual += (d.measure || 0);
        resource.currentMonthForecast += (d.forecast || 0);
      }
      
      // Check if this data point is in the previous month
      if (itemMonth === previousMonth) {
        // Previous month: only sum actual costs (measure)
        resource.previousMonthActual += (d.measure || 0);
      }
    });

    // Convert to array and sort by resource name
    const resources = Array.from(resourceMap.values())
      .sort((a, b) => (a.resource || '').localeCompare(b.resource || ''));

    // Create result rows - one per resource
    const result = resources.map((resource) => {
      // Total cost for current month = actual + forecast
      const currentMonthTotal = resource.currentMonthActual + resource.currentMonthForecast;
      
      // Change: current month (actual + forecast) vs previous month (actual)
      const change = currentMonthTotal - resource.previousMonthActual;
      const changePercentage = resource.previousMonthActual === 0
        ? 0
        : (change / resource.previousMonthActual) * 100;

      // Trend: based on current month's actual + forecast costs
      // For trend, we compare current month total to previous month actual
      // This gives us the direction of change
      const trendChange = change;

      return {
        grouping: resource.resource, // Resource name as the grouping (first column)
        previous_month_actual_cost: resource.previousMonthActual, // Actual cost in previous month
        current_month_actual_cost: resource.currentMonthActual, // Actual cost in current period only
        forecasted_cost: currentMonthTotal, // Actual + forecast for current month
        forecast_change: change, // Current month (actual+forecast) vs previous month (actual)
        forecast_change_percentage: changePercentage,
        forecast_trend: trendChange, // Used for trend indicator
      };
    });

    return result;
  }, [data]);

  // Get selected measures or use defaults
  const selectedMeasures = Array.isArray(config?.measures) && config.measures.length > 0
    ? config.measures
    : DEFAULT_FORECAST_TABLE_MEASURES;

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      {transformedData && transformedData.length > 0 ? (
        <TableComponent
          data={transformedData}
          grouping={config.grouping || "Resource"}
          title={config.title}
          showTitle={config.showTitle}
          measures={selectedMeasures}
          endpoint={config?.dataSource?.endpoint}
          showIcons={config.showIcons === true}
          showTotalRow={config.showTotalRow === true}
        />
      ) : !isLoading && !error && (
        <WidgetEmptyState 
          title="No forecast data available"
          description="Try adjusting your filters or date range"
          icon={tableIcon}
        />
      )}
    </BaseWidget>
  );
};

ForecastTableWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    showTitle: PropTypes.bool,
    dateFormat: PropTypes.string,
    dateGrouping: PropTypes.object,
    grouping: PropTypes.string,
    measures: PropTypes.arrayOf(PropTypes.string),
    showIcons: PropTypes.bool,
    showTotalRow: PropTypes.bool,
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool,
  onFilterAdd: PropTypes.func
};

export default ForecastTableWidget;

